/**
 * FrameworkHome -- Framework + CAP module home (P33.2).
 *
 * Replaces the v0.1.0 `HelloFramework` landing with the same shape
 * as TwinHome (P32.2) + FoundationHome (P33.1):
 *
 *   ModuleRubric ("Framing - Framework + CAP")
 *   h1 + 1-line positioning
 *   Signals strip (work-in-flight, not live state)
 *   3 INTENT-grouped tile sections:
 *     YOUR WORK    -- 3 position queue tiles (Preparer / Reviewer
 *                     / Approver) -- Framework HAS queue-shaped
 *                     work (CAPs), so we lead with it
 *     ASSESSMENTS  -- the Frameworks hub + per-framework cycles
 *     LETTERS      -- regulator letter intake + recent letters
 *
 * The header's PositionQueueWidget already shows the queue
 * counts; the YOUR WORK section repeats them at larger affordance
 * with CTAs since this is the actual work surface. NOT a
 * duplication -- the header chip is "you have N to do across all
 * modules"; the body tile is "open this specific queue."
 *
 * Module-boundary rule (eslint): framework/* may import from
 * framework/* + shared/* only.
 */
import { useTranslation } from "react-i18next";
import { Arrow } from "shared/components/Arrow";
import { Link } from "react-router-dom";

import { ALL_POSITIONS } from "shared/api/types";
import { useUserPreferences } from "shared/api/userPreferences";
import { hasPosition, useMe } from "shared/api/useMe";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { ModuleRubric, Tile, TileSection } from "shared/layout/ModuleHomeTile";
import { ViewRail } from "shared/onboarding/ViewRail";
import { useCapCounts, useLetters } from "framework/api/hooks";

function WorkSignalsStrip() {
  const { t } = useTranslation();
  const counts = useCapCounts();
  const letters = useLetters();
  const totalCaps = counts.data
    ? Object.values(counts.data).reduce((a, b) => a + b, 0)
    : null;
  const letterCount = letters.data?.length ?? null;
  const loading = counts.isLoading || letters.isLoading;
  return (
    <section
      data-testid="framework-signals-strip"
      data-state={loading ? "loading" : "ready"}
      className="rounded-lg border border-slate-200 bg-white px-4 py-3"
    >
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm">
        {loading && (
          <span className="text-slate-500 text-xs">
            {t("modules.framework.home.signals.loading")}
          </span>
        )}
        {!loading && (
          <>
            <span data-testid="framework-stat-caps" className="text-slate-800">
              <strong className="tabular-nums">{totalCaps ?? "-"}</strong>{" "}
              {t("modules.framework.home.signals.openCaps", { count: totalCaps ?? 0 })}
            </span>
            <span data-testid="framework-stat-letters" className="text-slate-800">
              <strong className="tabular-nums">{letterCount ?? "-"}</strong>{" "}
              {t("modules.framework.home.signals.lettersOnFile", {
                count: letterCount ?? 0,
              })}
            </span>
          </>
        )}
      </div>
    </section>
  );
}

/**
 * Position queue tile -- adapted from the original HelloFramework
 * cards. Larger affordance than the header chip + visual cue when
 * the current user actually HOLDS the position.
 */
function PositionQueueTile({
  position,
  count,
  held,
}: {
  position: "preparer" | "reviewer" | "approver";
  count: number;
  held: boolean;
}) {
  const { t } = useTranslation();
  const label = position.charAt(0).toUpperCase() + position.slice(1);
  return (
    <Link
      to={`/framework/queue/${position}`}
      data-testid={`framework-tile-queue-${position}`}
      data-position={position}
      data-held={held}
      style={{ boxShadow: "inset 0 -3px 0 #C8A24A" }}
      className={[
        "block rounded-lg border p-4 transition",
        held
          ? "border-brand-brass bg-brand-brass/10 hover:border-brand-brass hover:shadow-md"
          : "border-slate-200 bg-white hover:border-slate-400 hover:shadow-md",
      ].join(" ")}
    >
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-500">
        <MarsadIcon name="dual-signoff" size={13} className="text-brand-brassDark" />
        {label}
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span
          data-testid={`framework-queue-count-${position}`}
          className="text-3xl font-semibold text-slate-900 tabular-nums"
        >
          {count}
        </span>
        <span className="text-xs text-slate-500">
          {t("modules.framework.home.signals.openCaps", { count })}
        </span>
      </div>
    </Link>
  );
}

/** P115.5: "resume where you left off" -- links to the position queue
 *  the user last worked in (default-landing hint, not an auto-redirect). */
function ResumeBanner() {
  const { t } = useTranslation();
  const prefs = useUserPreferences();
  const role = prefs.data?.last_role_used;
  if (!role || !(ALL_POSITIONS as readonly string[]).includes(role)) return null;
  const label = role.charAt(0).toUpperCase() + role.slice(1);
  return (
    <Link
      data-testid="framework-resume"
      data-role={role}
      to={`/framework/queue/${role}`}
      className="block rounded-lg border border-brand-brass/40 bg-brand-brass/5 px-4 py-2 text-sm text-brand-brassDark hover:bg-brand-brass/10"
    >
      {t("modules.framework.home.resume.prefix")} &mdash;{" "}
      <span className="font-semibold">{label}</span>{" "}
      {t("modules.framework.home.resume.queue")} <Arrow />
    </Link>
  );
}

export function FrameworkHome() {
  const { t } = useTranslation();
  const me = useMe();
  const counts = useCapCounts();

  return (
    <article data-testid="framework-home" className="max-w-6xl space-y-6">
      <ViewRail viewKey="framework" />
      <header>
        <ModuleRubric>{t("modules.framework.home.rubric")}</ModuleRubric>
        <h1
          data-testid="framework-home-heading"
          className="mt-1 font-display text-2xl font-semibold text-slate-900"
        >
          {t("modules.framework.home.heading")}
        </h1>
        <p
          data-testid="framework-home-positioning"
          className="mt-1 text-sm text-slate-700 max-w-3xl"
        >
          {t("modules.framework.home.positioning")}
        </p>
      </header>

      <WorkSignalsStrip />

      <ResumeBanner />

      <TileSection
        heading={t("modules.framework.home.sections.yourWork")}
        testid="framework-section-your-work"
      >
        <Tile
          to="/recommendations"
          name={t("modules.framework.home.tiles.recommendations.name")}
          desc={t("modules.framework.home.tiles.recommendations.desc")}
          testid="framework-tile-recommendations"
          icon="recommendation"
        />
        {ALL_POSITIONS.map((position) => (
          <PositionQueueTile
            key={position}
            position={position}
            count={counts.data?.[position] ?? 0}
            held={me.data ? hasPosition(me.data, position) : false}
          />
        ))}
      </TileSection>

      <TileSection
        heading={t("modules.framework.home.sections.assessments")}
        testid="framework-section-assessments"
      >
        <Tile
          to="/framework/assessments"
          name={t("modules.framework.home.tiles.assessmentsHub.name")}
          desc={t("modules.framework.home.tiles.assessmentsHub.desc")}
          testid="framework-tile-assessments-hub"
          icon="framework"
        />
        <Tile
          to="/framework/attestations"
          name={t("modules.framework.home.tiles.attestations.name")}
          desc={t("modules.framework.home.tiles.attestations.desc")}
          testid="framework-tile-attestations"
          icon="attestation"
        />
        <Tile
          to="/framework/regulatory-change"
          name={t("modules.framework.home.tiles.regChange.name")}
          desc={t("modules.framework.home.tiles.regChange.desc")}
          testid="framework-tile-reg-change"
          icon="obligation"
        />
        <Tile
          to="/framework/outbound-feed"
          name={t("modules.framework.home.tiles.outboundFeed.name")}
          desc={t("modules.framework.home.tiles.outboundFeed.desc")}
          testid="framework-tile-outbound-feed"
          icon="verifier"
        />
        <Tile
          to="/framework/document-gap"
          name={t("modules.framework.home.tiles.documentGap.name")}
          desc={t("modules.framework.home.tiles.documentGap.desc")}
          testid="framework-tile-document-gap"
          icon="document-locker"
        />
      </TileSection>

      <TileSection
        heading={t("modules.framework.home.sections.letters")}
        testid="framework-section-letters"
      >
        <Tile
          to="/framework/letters/new"
          name={t("modules.framework.home.tiles.letterNew.name")}
          desc={t("modules.framework.home.tiles.letterNew.desc")}
          testid="framework-tile-letter-new"
          icon="obligation"
        />
      </TileSection>
    </article>
  );
}
